import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  bufferTime,
  catchError,
  concat,
  debounceTime,
  defaultIfEmpty,
  EMPTY,
  expand,
  filter,
  groupBy,
  map,
  merge,
  mergeAll,
  mergeMap,
  NEVER,
  Observable,
  of,
  reduce,
  switchMap,
  take,
  takeUntil
} from 'rxjs'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import { getHub } from '../../helpers/hub'
import { resetCheckedOrders } from '../checkedOrders/actions'
import { defaultFilter } from '../filter/types'
import { State as OrderState } from '../order/reducer'
import { State as StagedOrdersState } from '../stagedOrders/reducer'
import { getSecurityIdsWithStagedOrLiveOrder } from '../stagedOrders/selectors'
import {
  getDetailsForWatchlist,
  getWatchlistDetails
} from '../watchList/selectors'
import { logError } from '../ws/actions'
import {
  addOrUpdateListTradingSecurities,
  addOrUpdateSecurities,
  addOrUpdateSecuritiesCache,
  ApplyQuickFiltersAction,
  FetchExportSecuritiesAction,
  fetchExportSecuritiesFailed,
  fetchExportSecuritiesSucceeded,
  fetchGridViewState,
  FetchGridViewStateAction,
  GetAllSecuritiesAction,
  getAllSecuritiesFailed,
  getAllSecuritiesSucceeded,
  fetchGridViewStateFailed,
  fetchGridViewStateSucceeded,
  FetchListTradingSecuritiesAction,
  fetchListTradingSecuritiesFailure,
  FetchSecuritiesByIdsAction,
  RemoveGridAction,
  resetSortToTop,
  ResetSortToTopTriggerActions,
  SaveAdvancedFilterAction,
  SecuritiesAction,
  SetIsMine,
  setIssuerFilter,
  SetIssuerFilterAction,
  SetQueryFilterAction,
  SetQuoteReliability,
  SetSecuritiesFilterAction,
  SetShowLive,
  SetShowRfqEligibleAction,
  SetWatchListId,
  SortValidationsToTopAction,
  subscribeGridViewport,
  UnsubscribeListTradingAction
} from './actions'
import { subscribeGridViewportDefinition } from './definition'
import {
  createSecurityFilter,
  createSecurityFromResponse,
  DYNAMIC_WATCHLISTS,
  GetSecuritiesFilter,
  getTypedArray,
  SECURITIES_BUFFER_DELAY,
  SecurityResponse,
  SILENT_PAGE_SIZE
} from './helpers'
import { State } from './reducer'
import { State as UserState } from '../users/reducer'
import { State as WatchlistState } from '../watchList/reducer'
import { getGridDefinition } from './selectors'
import { QuoteReliability } from './types'
import { getSelectedUser } from '../users/selectors'
import { getTypedProperty } from '../helpers'

const notEmpty = <T>(array: T[] | null | undefined): array is T[] =>
  !!array && array.length > 0

export const defaultSecuritiesFilter = {
  InWatchlistId: undefined,
  IssuerSymbol: undefined,
  Query: undefined,
  SecurityId: undefined,
  showLive: false,
  QuoteReliability: QuoteReliability.All,
  sortToTop: [],
  MyFirm: false,
  MyBook: false,
  filter: defaultFilter,
  size: 0,
  IsMine: false,
  isTicker: false,
  tickerAndMaturity: false
}

type CombinedState = {
  securities: State
  order: OrderState
  stagedOrders: StagedOrdersState
  users: UserState
  watchList: WatchlistState
}

const resetCheckedOrdersWhenChangingWatchlistEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType(
      'securities.setWatchListId',
      'securities.setIssuerFilter',
      'securities.setIsMine',
      'securities.setShowRfqEligible',
      'securities.setShowLive',
      'securities.setQuoteReliability',
      'securities.setSecuritiesFilter',
      'securities.applyQuickFilters'
    ),
    map(() => resetCheckedOrders())
  )

const fetchSecuritiesByIdsEpic: Epic<Action> = (action$) => {
  return action$.pipe(
    ofType('securities.fetchSecuritiesByIds'),
    mergeMap((action: FetchSecuritiesByIdsAction) => {
      if (action.payload.length === 0) {
        return EMPTY
      }

      return getHub()
        .stream('GetSecuritiesById', action.payload, {})
        .pipe(
          bufferTime(SECURITIES_BUFFER_DELAY),
          filter(notEmpty),
          map((securities) =>
            addOrUpdateSecuritiesCache(
              securities.map(createSecurityFromResponse)
            )
          ),
          take(1),
          catchError((err) => of(logError(err)))
        )
    })
  )
}

const clearSearchOnSetNewWatchlistEpic: Epic<SecuritiesAction> = (action$) =>
  action$.pipe(
    ofType<SetWatchListId>('securities.setWatchListId'),
    map((action) => setIssuerFilter(action.payload.gridIndex, undefined))
  )

const resetSortToTopOnFilterChangeEpic: Epic<Action> = (action$) =>
  action$.pipe(
    ofType<ResetSortToTopTriggerActions>(
      'securities.setWatchListId',
      'securities.setSecuritiesFilter',
      'securities.setIssuerFilter',
      'securities.setIsMine'
    ),
    map((action) => resetSortToTop(action.payload.gridIndex))
  )

const setSelectedUserEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('users.setSelectedUser'),
    mergeMap(() => {
      const gridDefinitions = state$.value.securities.gridDefinitions
      return Object.keys(gridDefinitions).flatMap((gridIndexStr) => {
        const gridIndex = Number(gridIndexStr)
        return [
          subscribeGridViewport(gridIndex, []),
          fetchGridViewState(gridIndex)
        ]
      })
    })
  )

const LT_SECURITY_PAGE_SIZE = 200
const fetchListTradingSecuritiesEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('securities.fetchListTradingSecurities'),
    switchMap((action: FetchListTradingSecuritiesAction) => {
      const watchlist = getWatchlistDetails(state$.value)(
        action.payload.watchlistId
      )
      if (!watchlist) {
        return of(fetchListTradingSecuritiesFailure())
      }
      const securitiesFilter = {
        ...defaultSecuritiesFilter,
        filter: watchlist.filter || defaultFilter,
        InWatchlistId: action.payload.watchlistId,
        MyFirm: watchlist?.myFirmChecked ?? false
      }
      const $stream = getHub()
        .stream<SecurityResponse[]>(
          'GetSecurities',
          state$.value.users.selectedUser,
          securitiesFilter,
          action.payload.page * LT_SECURITY_PAGE_SIZE,
          LT_SECURITY_PAGE_SIZE,
          false
        )
        .pipe(
          bufferTime(SECURITIES_BUFFER_DELAY),
          filter<SecurityResponse[][]>(notEmpty),
          map((updatesArray) => {
            return updatesArray.reduce(
              (acc, updates) => [...acc, ...updates],
              []
            )
          })
        )
      return $stream.pipe(
        map((securities: SecurityResponse[]) =>
          addOrUpdateListTradingSecurities(
            securities.map(createSecurityFromResponse),
            action.payload.page
          )
        ),
        takeUntil(
          action$.pipe(
            ofType<UnsubscribeListTradingAction>(
              'securities.unsubscribeListTrading'
            )
          )
        ),
        catchError((err) =>
          of(fetchListTradingSecuritiesFailure(), logError(err))
        )
      )
    })
  )

const emptySecurities: SecurityResponse[] = []

const getPageOfFilteredSecurities$ = (
  start: number,
  pageSize: number,
  gsf: Partial<GetSecuritiesFilter>,
  isDynamicWatchlist: boolean,
  useOrderData: boolean,
  user: number = 0
) => {
  const serviceName = useOrderData ? 'GetSecurities' : 'GetStaticData'
  return getHub()
    .stream<SecurityResponse[]>(
      serviceName,
      user,
      gsf,
      start,
      pageSize,
      isDynamicWatchlist
    )
    .pipe(take(1))
}

const allSecuritiesFilter = {
  ...defaultSecuritiesFilter,
  ShowRFQEligible: false,
  showLive: undefined
} as const

const getAllPagesOfFilteredSecurities$ = (
  start: number,
  pageSize: number,
  gsf: Partial<GetSecuritiesFilter>,
  isDynamicWatchlist: boolean,
  useOrderData: boolean,
  user: number = 0
) => {
  return of(emptySecurities).pipe(
    // recurse until we hit a page that is not full
    expand((res, i) => {
      if (i > 0 && res.length < pageSize) {
        // stop recursion. we have loaded all pages
        return EMPTY
      }
      return getPageOfFilteredSecurities$(
        start + i * pageSize,
        pageSize,
        gsf,
        isDynamicWatchlist,
        useOrderData,
        user
      ) as Observable<SecurityResponse[]>
    }),
    filter(notEmpty),
    defaultIfEmpty(emptySecurities)
  )
}

const getAllSecuritiesForGrid$ = (
  gridIndex: number,
  useOrderData: boolean,
  state: CombinedState
): Observable<SecurityResponse[]> => {
  const user = getSelectedUser(state)
  const gridDefinition = getGridDefinition(state)(gridIndex)
  const watchlistIdSelected = gridDefinition?.watchlistId
  const watchlistsDetails =
    watchlistIdSelected && getDetailsForWatchlist(state)(watchlistIdSelected)

  const isMineSelected = getTypedProperty(gridDefinition, 'isMine') ?? false

  const mySecurities = isMineSelected
    ? getSecurityIdsWithStagedOrLiveOrder(state)
    : undefined

  const isDynamicWatchlist =
    !!watchlistsDetails && DYNAMIC_WATCHLISTS.includes(watchlistsDetails.name)

  const securityFilter = createSecurityFilter(gridDefinition, mySecurities)
  return Array.isArray(securityFilter)
    ? getHub()
        .stream<SecurityResponse[]>('GetSecuritiesById', securityFilter, {})
        .pipe(
          bufferTime<SecurityResponse[]>(SECURITIES_BUFFER_DELAY),
          filter(notEmpty),
          mergeAll(),
          take(1),
          // server can return single result not wrapped in an array, if so wrap
          map((securities) =>
            Array.isArray(securities) ? securities : [securities]
          )
        )
    : getAllPagesOfFilteredSecurities$(
        0,
        SILENT_PAGE_SIZE,
        securityFilter,
        isDynamicWatchlist,
        useOrderData,
        user
      )
}

const fetchExportSecuritiesEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<FetchExportSecuritiesAction>('securities.fetchExportSecurities'),
    switchMap((action) => {
      const state = state$.value
      const { gridIndex } = action.payload

      return getAllSecuritiesForGrid$(gridIndex, true, state).pipe(
        reduce(
          (acc, curr) => [...acc, ...curr],
          getTypedArray<SecurityResponse>()
        ),
        mergeMap((securities) => {
          return of(
            fetchExportSecuritiesSucceeded(
              securities.map(createSecurityFromResponse)
            )
          )
        }),
        catchError((err) => of(fetchExportSecuritiesFailed(), logError(err)))
      )
    })
  )

const fetchSecuritiesForGridViewEpic: Epic<Action, Action, CombinedState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType<FetchGridViewStateAction>('securities.fetchGridViewState'),
    groupBy(
      (action) => action.payload,
      undefined,
      (group$) =>
        merge(
          action$.pipe(
            ofType<RemoveGridAction>('securities.removeGrid'),
            filter((a) => a.payload.gridIndex === group$.key)
          ),
          NEVER
        )
    ),
    mergeMap((group$) =>
      group$.pipe(
        switchMap((action) => {
          const state = state$.value
          const gridIndex = action.payload
          return getAllSecuritiesForGrid$(gridIndex, false, state).pipe(
            mergeMap((securities) => {
              return of(
                addOrUpdateSecurities(
                  gridIndex,
                  securities.map(createSecurityFromResponse),
                  0
                ),
                fetchGridViewStateSucceeded(
                  gridIndex,
                  securities.map((s) => s.id)
                )
              )
            }),
            catchError((err) =>
              of(fetchGridViewStateFailed(gridIndex, err), logError(err))
            )
          )
        })
      )
    )
  )

const fetchGridViewEpic: Epic = (action$) =>
  action$.pipe(
    ofType<
      | SetIssuerFilterAction
      | SetIsMine
      | SetShowLive
      | SetShowRfqEligibleAction
      | SetQuoteReliability
      | SetSecuritiesFilterAction
      | SetQueryFilterAction
      | SortValidationsToTopAction
      | SaveAdvancedFilterAction
      | ApplyQuickFiltersAction
    >(
      'securities.setIssuerFilter',
      'securities.setIsMine',
      'securities.setShowLive',
      'securities.setShowRfqEligible',
      'securities.setQuoteReliability',
      'securities.setSecuritiesFilter',
      'securities.setQueryFilter',
      'securities.sortValidationsToTopAction',
      'securities.saveAdvancedFilter',
      'securities.applyQuickFilters'
    ),
    groupBy(
      (action) => action.payload.gridIndex,
      undefined,
      (group$) =>
        merge(
          action$.pipe(
            ofType<RemoveGridAction>('securities.removeGrid'),
            filter((a) => a.payload.gridIndex === group$.key)
          ),
          NEVER
        )
    ),
    mergeMap((group$) =>
      group$.pipe(
        debounceTime(500), // these all get set individually when a grid loads (for now)
        switchMap((action) => {
          const { gridIndex } = action.payload
          return of(
            subscribeGridViewport(gridIndex, []),
            fetchGridViewState(gridIndex)
          )
        })
      )
    )
  )

const getAllSecuritiesEpic: Epic<Action, Action, CombinedState> = (action$) =>
  action$.pipe(
    ofType<GetAllSecuritiesAction>('securities.getAllSecurities'),
    switchMap(() =>
      concat(
        getAllPagesOfFilteredSecurities$(
          0,
          SILENT_PAGE_SIZE,
          allSecuritiesFilter,
          false,
          false
        ).pipe(
          mergeMap((securities) =>
            of(
              addOrUpdateSecuritiesCache(
                securities.map(createSecurityFromResponse)
              )
            )
          )
        ),
        of(getAllSecuritiesSucceeded())
      ).pipe(catchError((err) => of(getAllSecuritiesFailed(), logError(err))))
    )
  )

export default combineEpics(
  clearSearchOnSetNewWatchlistEpic,
  fetchExportSecuritiesEpic,
  getAllSecuritiesEpic,
  fetchGridViewEpic,
  fetchListTradingSecuritiesEpic,
  fetchSecuritiesByIdsEpic,
  fetchSecuritiesForGridViewEpic,
  resetCheckedOrdersWhenChangingWatchlistEpic,
  resetSortToTopOnFilterChangeEpic,
  setSelectedUserEpic,
  createStreamEpic(
    'securities.subscribeGridViewport',
    subscribeGridViewportDefinition
  )
)
